import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { orderBy } from 'lodash';
import { Typography, Button, Grid, makeStyles } from '@material-ui/core';
import { TFunction } from 'i18next';
import { ModalContainer, ModalHeader, ModalFooter, ModalContent } from 'components/@common/Modal';
import Dropdown, { DropdownOption } from 'components/@common/Inputs/Dropdown';
import LwtTable, { ColumnProps, SortDir } from 'components/@common/Table/LwtTable';
import useParseActionRouteParams from 'hooks/route-action/use-parse-action-route-params';
import { LicenseType, Mastercode, Product } from 'types/schema-generated-types';
import { formatUiDate } from 'utilities/date-utils';
import getOrderNumber from 'utilities/get-order-number';

export type Props = {
  mastercodes: Mastercode[];
  onClose?: () => void;
};

interface OrderItemRow {
  product: Product;
  orderNumber: string;
  mastercode: Mastercode;
  licenseCount: number;
  dateExpiration?: string;
}

const LicenseDetailsModal = ({ mastercodes, onClose }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation('AdminProductLicenses');
  const { t: tCommon } = useTranslation('Common');
  const { product: productCodeParam } = useParseActionRouteParams();

  const options: DropdownOption[] = useMemo(() => {
    const productMap = new Map<string, Product>();
    for (const mastercode of mastercodes) {
      const orderItems = mastercode.orderItems ?? [];
      for (const orderItem of orderItems) {
        if (!orderItem.product || orderItem.licenseType === LicenseType.TRIAL) continue;
        productMap.set(orderItem.product.code, orderItem.product);
      }
    }

    const products = orderBy(Array.from(productMap.values()), 'name');

    return products.map(product => ({ label: product.name, value: product.code }));
  }, [mastercodes]);

  const defaultProductCode = productCodeParam ?? options?.[0].value ?? '';
  const [productCode, setProductCode] = useState(defaultProductCode);

  const handleProductChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newProductCode = event.target.value as string;
    setProductCode(newProductCode);
  };

  const getColumns: (t: TFunction) => ColumnProps<OrderItemRow>[] = useCallback(
    (t: TFunction) => [
      {
        id: 'product',
        label: t('detailsProductLabel'),
        sortKey: 'product.name',
        getValue: item => <Typography>{item.product.name}</Typography>,
        sortable: true
      },
      {
        id: 'orderNumber',
        label: t('detailsHeaderOrderNumber'),
        sortKey: 'orderNumber',
        getValue: item => <Typography>{item.orderNumber}</Typography>,
        sortable: true
      },
      {
        id: 'code',
        label: t('detailsHeaderCode'),
        sortKey: 'mastercode.code',
        getValue: item => <Typography>{item.mastercode.code}</Typography>,
        sortable: true
      },
      {
        id: 'count',
        label: t('detailsHeaderCount'),
        sortKey: 'licenseCount',
        getValue: item => <Typography>{item.licenseCount}</Typography>,
        sortable: true
      },
      {
        id: 'dateExpiration',
        label: t('detailsHeaderExpirationDate'),
        sortKey: 'dateExpiration',
        getValue: item => (
          <Typography>{item.dateExpiration ? formatUiDate(item.dateExpiration) : ''}</Typography>
        ),
        sortable: true
      }
    ],
    []
  );

  const columns: ColumnProps<any>[] = getColumns(t);

  const rows: OrderItemRow[] = [];
  for (const mastercode of mastercodes) {
    const orderItems = mastercode.orderItems ?? [];
    for (const orderItem of orderItems) {
      if (orderItem.product?.code !== productCode || orderItem.licenseType === LicenseType.TRIAL) {
        continue;
      }

      rows.push({
        product: orderItem.product!,
        orderNumber: orderItem.order ? getOrderNumber(orderItem.order) : '',
        mastercode,
        licenseCount: orderItem.licenseCount ?? 0,
        dateExpiration: orderItem.dateExpiration
      });
    }
  }

  return (
    <ModalContainer
      Header={
        <ModalHeader>
          <Typography variant="h2" component="h1">
            {t('licenseDetailsTitle')}
          </Typography>
        </ModalHeader>
      }
      Footer={
        <ModalFooter
          Actions={
            <Button variant="outlined" onClick={onClose}>
              {tCommon('Close')}
            </Button>
          }
        />
      }
    >
      <ModalContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Dropdown
              id="details-product-select"
              label={t('detailsProductLabel')}
              options={options}
              handleChange={handleProductChange}
              value={productCode}
              className={classes.productSelect}
            />
          </Grid>
          <Grid item xs={12}>
            <LwtTable
              columns={columns}
              data={rows}
              options={{
                sortable: true,
                defaultSort: { sortKey: 'dateExpiration', direction: SortDir.ASC }
              }}
              classes={{
                table: () => classes.table,
                headRow: () => classes.tableHeader,
                body: () => classes.tableBody,
                row: () => classes.tableRow
              }}
            />
          </Grid>
        </Grid>
      </ModalContent>
    </ModalContainer>
  );
};

const useStyles = makeStyles(theme => ({
  productSelect: {
    maxWidth: 400
  },
  table: {
    '& th': {
      padding: theme.spacing(1)
    },
    // Janky way to right align numeric columns
    '& th:last-child, & th:nth-last-child(2)': {
      justifyItems: 'flex-end'
    }
  },
  tableHeader: {
    textTransform: 'none'
  },
  tableBody: {
    borderRadius: 4,
    boxShadow: '0 0 0 1px #e0e0e0',
    '& > tr:last-child > td': {
      borderBottom: 'none'
    }
  },
  tableRow: {
    '& > td': {
      padding: theme.spacing(1)
    },
    // Janky way to right align numeric columns
    '& td:last-child, & td:nth-last-child(2)': {
      justifyItems: 'flex-end',
      paddingRight: theme.spacing(3)
    }
  }
}));

export default LicenseDetailsModal;
